import { useMemo } from 'react';
import {
  buildThreadsFromCorrespondence,
  buildUnifiedChatThread,
  filterThreads,
  sortThreadsByActivity
} from '../services/thread.service';
import type {
  ChatThread,
  InstitutionCorrespondence,
  ThreadFilters
} from '../types';

/**
 * Hook to transform correspondence into filtered and sorted chat threads
 * Can return either multiple threads or a unified single thread
 */
export function useChatThreads(
  correspondence: InstitutionCorrespondence[],
  institutionName: string,
  filters: ThreadFilters = {},
  useUnifiedView = false
): { threads: ChatThread[]; unifiedThread: ChatThread | null } {
  return useMemo(() => {
    if (useUnifiedView) {
      // Build a single unified thread with all messages
      const unifiedThread = buildUnifiedChatThread(
        correspondence,
        institutionName,
        0
      );

      // Apply search filter to unified thread if needed
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        const filteredMessages = unifiedThread.messages.filter(
          (msg) =>
            msg.content.toLowerCase().includes(searchLower) ||
            msg.sender.toLowerCase().includes(searchLower)
        );

        return {
          threads: [],
          unifiedThread:
            filteredMessages.length > 0
              ? { ...unifiedThread, messages: filteredMessages }
              : null
        };
      }

      return { threads: [], unifiedThread };
    } else {
      // Build multiple threads from correspondence
      const threads = buildThreadsFromCorrespondence(
        correspondence,
        institutionName
      );

      // Apply filters
      const filtered = filterThreads(threads, filters);

      // Sort by last activity
      const sorted = sortThreadsByActivity(filtered);

      return { threads: sorted, unifiedThread: null };
    }
  }, [correspondence, institutionName, filters, useUnifiedView]);
}
